import React, {useCallback, useEffect} from 'react';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { FormSwitch } from '../FormInputs';
import { FormSelect } from '../FormInputs';
import { RangeSlider } from '../RangeSlider/RangeSlider';
import { useCurrentUser } from '../../contexts/CurrentUser';
import { topicModelingTranslationLanguageOptions } from '../../utils/languages';
import { API } from '../../API';
import { showRequestError } from '../../utils/requestError';

interface StoriesSubgroupProps {
  form: any;
}

export const StoriesSubgroup: React.FC<StoriesSubgroupProps> = ({ form }) => {
  const { t } = useTranslation();
  const [currentUser] = useCurrentUser();
  const [storiesVectorsModelOptions, setStoriesVectorsModelOptions] = React.useState<{ value: string | null; label: string }[]>([]);

  const workspaceConfig = currentUser?.workspace?.config;
  const featureFlags = workspaceConfig?.featureflags;
  const showStoriesParameters = featureFlags?.includes('show_stories_parameters');

  const createStories = form.state.values.createStories;
  const includeCommentsToStories = form.state.values.includeCommentsToStories;
  const storiesCandidatesMinThreshold = form.state.values.storiesCandidatesMinThreshold;
  const storiesGroupingMaxDistance = form.state.values.storiesGroupingMaxDistance;

  const getStoriesVectorsModelOptions = useCallback(() => {
      API.fetch('GET', `/API/v1/config/vectorization_models`).then((data) => {
        let default_value = { value: null, label: 'Default' };

        setStoriesVectorsModelOptions([
          default_value,
          ...Object.entries(data.models)
            .map(([name, code]) => ({ value: code as string, label: name }))
        ]);
      }).catch((error) => showRequestError(error));
    }, []);
  
    useEffect(getStoriesVectorsModelOptions, [getStoriesVectorsModelOptions]);

  if (!showStoriesParameters || !createStories) {
    return null;
  }

  return (
    <div className="form-element-group">
      <FormSwitch
        form={form}
        name="includeChatMessagesToStories"
        label={t('Include chat messages to stories')}
        disabled={!createStories}
        className="stories-grouping-switch"
      />

      <div className='divider' />

      <FormSwitch
        form={form}
        name="includeCommentsToStories"
        label={t('Include comments to stories')}
        disabled={!createStories}
        className="stories-grouping-switch"
      />

      <div className='divider' />

      <form.Subscribe
        selector={(state: any) => state.values.enableStoriesSummarization}
        children={(enableStoriesSummarization: any) => {
          return (
            <>
              <FormSwitch
                form={form}
                name="enableStoriesSummarization"
                label={t('Summaries of stories')}
                subLabel={t('AI generates concise summaries for each story.')}
                disabled={!createStories}
              />

              {enableStoriesSummarization && (
                <FormSelect
                  form={form}
                  name="storiesTranslationLanguage"
                  label={t('Translate summaries into')}
                  placeholder={t('Choose language')}
                  options={topicModelingTranslationLanguageOptions(t)}
                  isClearable={false}
                />
              )}
            </>
          );
        }}
      />

      <div className='divider' />

      <div className="form-element">
        <label htmlFor="storiesVectorsModel">
          {t('Vectorization model to use')}
        </label>
        <form.Field
          name="storiesVectorsModel"
          children={(field: any) => (
            <Select
              className="react-select-container"
              classNamePrefix="react-select"
              options={storiesVectorsModelOptions}
              value={storiesVectorsModelOptions.find(
                (option) => option.value === field.state.value,
              )}
              onChange={(value) => field.handleChange(value)}
              onBlur={field.handleBlur}
              placeholder={t('Choose vectorization model for stories')}
              isDisabled={!createStories}
              isClearable={true}
              isSearchable={true}
              name="storiesVectorsModel"
            />
          )}
        />
      </div>

      <div className="form-element">
        <label htmlFor="stories_candidates_min_threshold">
          {t('Candidates min threshold')}
        </label>
        <form.Field
          name="storiesCandidatesMinThreshold"
          children={(field: any) => (
            <RangeSlider
              min={0.05}
              max={1}
              step={0.05}
              data={field.state.value || 0.8}
              width="100%"
              onChange={(value: any) => field.handleChange(value)}
              disabled={!createStories}
            />
          )}
        />
      </div>

      <div className='divider' />

      <FormSwitch
        form={form}
        name="useStoriesGroupingMaxDistance"
        label={t('Use max grouping distance')}
        disabled={!createStories}
        // className="stories-grouping-switch"
      />

      <form.Field
        name="useStoriesGroupingMaxDistance"
        children={(switchField: any) =>
          switchField.state.value && (
            <div className="form-element">
              <label htmlFor="stories_grouping_max_distance">
                {t('Grouping max distance')}
              </label>
              <form.Field
                name="storiesGroupingMaxDistance"
                children={(field: any) => (
                  <RangeSlider
                    min={0.05}
                    max={1}
                    step={0.05}
                    data={field.state.value || 0.64}
                    width="100%"
                    onChange={(value: any) => field.handleChange(value)}
                    disabled={!switchField.state.value || !createStories}
                  />
                )}
              />
            </div>
          )
        }
      />
    </div>
  );
};